/**
 * Validates if a string is a valid number format
 * Allows empty string, integers, decimals, and scientific notation (e.g., 1e+10)
 * Requires at least one digit (rejects standalone "." which parseFloat converts to NaN)
 *
 * @param value - The string to validate
 * @returns True if valid number format, false otherwise
 *
 * @example
 * isValidNumber("123") // true
 * isValidNumber("123.456") // true
 * isValidNumber(".5") // true
 * isValidNumber("5.") // true
 * isValidNumber("1e+10") // true
 * isValidNumber(".") // false (no digits - would produce NaN)
 * isValidNumber("abc") // false
 */
export const isValidNumber = (value: string): boolean => {
  // The lookahead (?=.*\d) ensures at least one digit exists
  // This prevents "." from being accepted (parseFloat(".") returns NaN)
  return value === "" || /^(?=.*\d)\d*\.?\d*(e\+\d+)?$/.test(value);
};

/**
 * Sanitizes a string input to ensure it's a valid number format
 * Removes invalid characters and fixes common formatting issues
 *
 * @param value - The string to sanitize
 * @returns Sanitized number string
 *
 * @example
 * sanitizeValue("123abc") // "123"
 * sanitizeValue("1.2.3") // "1.23"
 * sanitizeValue("03") // "3"
 * sanitizeValue("00123") // "123"
 * sanitizeValue("001.23") // "1.23"
 * sanitizeValue("00.3") // "0.3"
 */
export const sanitizeValue = (value: string): string => {
  return value
    .replace(/[^0-9.e+]/g, "") // Remove non-numeric characters except . e +
    .replace(/(\..*)\./g, "$1") // Remove extra decimal points
    .replace(/(e\+.*)(e\+)/gi, "$1") // Remove extra scientific notation
    .replace(/^0+(?=[1-9])/, "") // Remove leading zeros before non-zero digits (03 → 3, 001 → 1)
    .replace(/^0+(?=0\.)/, ""); // Remove leading zeros before 0. (00.3 → 0.3)
};
